RivalIQ.apps.Insights.module('widgets', function (widgets, App, Backbone, Marionette, $, _) {

    'use strict';

    const PIE_CHART_SIZE = 130;

    const MIN_NUM_POSTS_PER_BUCKET_FOR_OBSERVATION = 5;
    const MIN_POST_OBSERVATION_DELTA_PCT = 0.25;
    const MAX_POST_OBSERVATIONS = 2;
    const MIN_NUM_STORY_DAYS_FOR_OBSERVATION = 3;
    const MIN_NUM_STORIES_FOR_OBSERVATION = 10;
    const MIN_TAPS_FORWARD_PER_STORY_DELTA_PCT = 0.25;
    const MIN_STORY_EXIT_RATE_DELTA_PCT = 0.25;
    const MIN_NUM_VIDEO_STORIES_FOR_OBSERVATION = 10;
    const VIDEO_TOTAL_COMPLETION_RATE_THRESHOLD = 0.25;


    const ObservationsView = Marionette.LayoutView.extend({
        template: '#insightsByTypeObservation',

        ui: {
            information: '.fa-info-circle'
        },

        behaviors: {
            Titles: RivalIQ.behaviors.Titles
        },

        serializeData: function () {
            const me = this;

            const observations = me.getOption('observations') || [];
            return {
                observations: observations
            };
        },

        onRender: function () {
            const me = this;
            me.ui.information.popover();
        }
    });

    const TableView = RivalIQ.apps.Dashboard.widgets.MetricTable.extend({

        getFetchAttributes: function () {
            const me = this;

            me.sortColumns = me.metricsCollection.getColumnSortOrder(me.getProp('orderBy'));

            return {
                orderBy: null
            };
        }
    });
    const ByTypeTable = RivalIQ.apps.Dashboard.widgets.TableWidget.extend({
        view: TableView,
        scheduledExportAdvancedSettingsEnabled: false,

        processProps: function (props) {
            const me = this;

            _.defaults(props, {
                sortable: true
            });

            App.utils.buildInsightsTableMetrics(me, {
                property: 'name',
                dimensions: [props.analysisProperty],
                sortable: false
            }, props);

            return props;
        }
    });
    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.INSIGHTS_BY_TYPE_TABLE, ByTypeTable);

    function pluralize(str) {
        return `${str}${str.endsWith('s') ? 'es' : 's'}`;
    }

    function getPluralObjectName(objectType, source) {
        if (objectType === RivalIQ.enums.insightsObjectType.post) {
            return `${pluralize(RivalIQ.data.channels.get(source).get('postName'))}`;
        } else {
            return 'frames';
        }
    }

    function getSingularObjectName(objectType, source) {
        if (objectType === RivalIQ.enums.insightsObjectType.post) {
            return RivalIQ.data.channels.get(source).get('postName');
        } else {
            return 'frame';
        }
    }

    const PieChartSummaryItem = RivalIQ.apps.Dashboard.views.SummaryItem.mixin({

        template: '#adsWidgetMetricSummaryItemTemplate',
        tagName: 'div',

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        className: 'metric-summary-item',

        serializeData: function () {
            const me = this;

            const formatter = RivalIQ.metric.format.expandedFormatFn(me.model.attributes);
            const formattedValue = formatter(me.model.get('value'));

            let objectName;
            if (me.model.get('value') === 1) {
                objectName = getSingularObjectName(me.getProp('objectType'), me.getProp('source'));
            } else {
                objectName = getPluralObjectName(me.getProp('objectType'), me.getProp('source'));
            }

            return {
                formattedValue: formattedValue,
                roundedFormattedValue: [formattedValue, objectName].join(' '),
                title: me.model.getTitle()
            };
        },

        templateHelpers: function () {
            return RivalIQ.utils.emoji.getTemplateHelpers();
        }
    });

    function getColor(typeName) {
        return _.get(RivalIQ.enums.postTypes, [typeName.toLowerCase(), 'color']) || RivalIQ.enums.colors.instagramHighContrast;
    }

    const PieLegendModel = Backbone.Model.extend({
        getColor: function () {
            const me = this;
            return getColor(me.get('name'));
        },

        getTitle: function () {
            const me = this;
            return me.get('name');
        }
    });

    function getPostObservationMetrics(source) {
        switch (source) {
            case RivalIQ.enums.sources.INSTAGRAM_INSIGHTS:
                return {
                    engagementTotalPerPostMetric: 'organic_engagement_total_per_post',
                    reachMetric: 'organic_reach_per_post',
                    engagementRateMetric: 'organic_engagement_rate_by_reach',
                    engagementRateByImpression: 'organic_engagement_rate_by_impression'
                };

            case RivalIQ.enums.sources.FACEBOOK_INSIGHTS:
                return {
                    engagementTotalPerPostMetric: 'organic_social_engagement_total_per_post',
                    reachMetric: 'organic_reach_per_post',
                    engagementRateMetric: 'facebook_engagement_rate'
                };

            case RivalIQ.enums.sources.TWITTER_INSIGHTS:
                return {
                    engagementTotalPerPostMetric: 'engagement_total_per_post',
                    reachMetric: 'impressions_per_post',
                    engagementRateMetric: 'engagement_rate_by_impression'
                };

            case RivalIQ.enums.sources.LINKEDIN:
                return {
                    engagementTotalPerPostMetric: 'engagement_total_per_post',
                    reachMetric: 'impressions_per_post',
                    engagementRateMetric: 'engagement_rate_by_impression'
                };

            default:
                return {};
        }
    }

    function calculateOtherBucket(source, observationMetrics, metricsByType, highestMetricType) {

        const postObservationMetrics = getPostObservationMetrics(source);

        const isNotHighestMetricType = function (metricResult) {
            const mediaType = metricResult[0];
            return (mediaType !== highestMetricType);
        };

        const otherPostCounts = _.filter(observationMetrics.count.getValue({
            format: false
        }), isNotHighestMetricType);

        const getAggregateSumOverPosts = function (metricName) {
            // sums metric x given x_per_post values
            // ex: sums organic_engagement_total given number of posts and organic_engagement_total_per_post
            return _.sumBy(observationMetrics[metricName].getValue({
                format: false
            }), function (metricResult) {
                // aggregate sum over all other types that aren't the highestMetricType
                if (isNotHighestMetricType(metricResult)) {
                    // find the number of posts for this type
                    const postCounts = _.find(otherPostCounts, function (result) {
                        return result[0] === metricResult[0];
                    });

                    // the total sum of x is x_per_post * num_posts, 0 if no posts of that type
                    return _.last(metricResult) * (_.last(postCounts) || 0);
                }

                return 0;
            });
        };

        const getOtherSumOfMetric = function (metricName) {
            // sums all values of metric x that aren't the highestMetricType
            return _.sumBy(observationMetrics[metricName].getValue({
                format: false
            }), function (metricResult) {
                if (isNotHighestMetricType(metricResult)) {
                    // metricResult format: ['type', value]
                    return _.last(metricResult);
                }

                return 0;
            });
        };

        const otherReachPerPost = _.filter(observationMetrics[postObservationMetrics.reachMetric].getValue({
            format: false
        }), isNotHighestMetricType);
        const otherBucket = {
            numPosts: _.sumBy(otherPostCounts, _.last)
        };

        if (otherBucket.numPosts > 0) {
            const otherTotalReach = _.sumBy(otherReachPerPost, function (reachValue) {
                return reachValue[1] * metricsByType[reachValue[0]].numPosts;
            });
            const otherEngagementTotal = _.sumBy(observationMetrics.engagement_total.getValue({
                format: false
            }), function (metricResult) {
                return (isNotHighestMetricType(metricResult) ? _.last(metricResult) : 0);
            });

            otherBucket[postObservationMetrics.reachMetric] = otherTotalReach / otherBucket.numPosts;
            otherBucket[postObservationMetrics.engagementTotalPerPostMetric] = otherEngagementTotal / otherBucket.numPosts;

            // facebook uses a slightly different engagement rate for this observation.
            if (source === RivalIQ.enums.sources.FACEBOOK_INSIGHTS) {
                const otherEngagedUsersTotal = getAggregateSumOverPosts('engaged_users_per_post');
                const otherReach = getOtherSumOfMetric('_reach_total');

                otherBucket[postObservationMetrics.engagementRateMetric] = otherEngagedUsersTotal / otherReach;
            } else if (source === RivalIQ.enums.sources.INSTAGRAM_INSIGHTS) {
                const organicEngagementSum = getAggregateSumOverPosts('organic_engagement_total_per_post');
                const organicImpressions = getOtherSumOfMetric('organic_impressions');

                otherBucket[postObservationMetrics.engagementRateByImpression] = organicEngagementSum / organicImpressions;
            } else {
                otherBucket[postObservationMetrics.engagementRateMetric] = otherEngagementTotal / otherTotalReach;
            }
        }

        return otherBucket;
    }

    const ByTypeAnalysisView = widgets.ByTypeAnalysisView = RivalIQ.views.LayoutView.mixin({
        template: '#insightsByTypeTemplate',
        childView: RivalIQ.apps.Dashboard.views.Widget,

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        dashboardModelEvents: {
            'change': 'reload'
        },

        regions: {
            analysisChart: '.x-analysis-chart',
            table: '.x-table',
            analysisChartLegend: '.x-analysis-chart-legend',
            observationsRegion: '.x-observations'
        },

        ui: {
            analysisLabel: '.x-analysis-label',
            analysisChart: '.x-analysis-section',
            analysisSection: '.x-analysis-section',
            table: '.x-table'
        },

        loadData: function () {
            const me = this;

            const props = me.getProps();
            const pieChartMetric = RivalIQ.metric.build(me.dashboardModel.baseMetric, {
                property: 'count',
                dimensions: ['timeAnalysisMode', props.analysisProperty, 'channel', 'companyId'],
                orderBy: null
            });


            let observationMetrics;

            if (props.objectType === RivalIQ.enums.insightsObjectType.post) {
                const observationMetricsToFetch = _.values(getPostObservationMetrics(me.getProp('source')));
                observationMetrics = _.map(['engagement_total', 'count'].concat(observationMetricsToFetch), function (property) {
                    return {
                        property: property,
                        dimensions: [props.analysisProperty],
                        orderBy: null
                    };
                });

                // NOTE these metrics are used to aggregate source specific metrics for the "other" bucket in the insights panel
                // ie engagement rate by impression, organic across multiple post types
                // add them here so we don't calculate insights for them
                if (props.source === RivalIQ.enums.sources.FACEBOOK_INSIGHTS) {
                    observationMetrics.push({
                        property: 'engaged_users_per_post',
                        dimensions: [props.analysisProperty],
                        orderBy: null
                    });
                    observationMetrics.push({
                        property: '_reach_total',
                        dimensions: [props.analysisProperty],
                        orderBy: null
                    });
                }

                if (props.source === RivalIQ.enums.sources.INSTAGRAM_INSIGHTS) {
                    observationMetrics.push({
                        property: 'organic_impressions',
                        dimensions: [props.analysisProperty],
                        orderBy: null
                    });
                }
            } else {
                observationMetrics = [
                    {
                        property: 'impressions_per_story',
                        dimensions: [props.analysisProperty],
                        orderBy: null
                    },
                    {
                        property: 'average_taps_forward_rate',
                        dimensions: [props.analysisProperty],
                        orderBy: null
                    },
                    {
                        property: 'exits_per_story',
                        dimensions: [props.analysisProperty],
                        orderBy: null
                    },
                    {
                        property: 'total_impressions',
                        dimensions: [props.analysisProperty],
                        mediaType: 'Video',
                        orderBy: null
                    },
                    {
                        property: 'total_exits',
                        dimensions: [props.analysisProperty],
                        mediaType: 'Video',
                        orderBy: null
                    },
                    {
                        property: 'total_taps_forward',
                        dimensions: [props.analysisProperty],
                        mediaType: 'Video',
                        orderBy: null
                    },
                    {
                        property: 'count',
                        dimensions: [props.analysisProperty],
                        orderBy: null
                    }
                ];

                if (!props.storyDay) {
                    observationMetrics.push({
                        property: 'days_with_story',
                        concept: RivalIQ.metrics.descriptions.INSTAGRAM_STORY_DAYS.id,
                        orderBy: null
                    });
                }
            }

            let observationMetricBase = me.dashboardModel.baseMetric;
            if (me.getProp('source') === RivalIQ.enums.sources.TWITTER_INSIGHTS) {
                // Twitter observations should always exclude retweets since we don't get engagement or impressions data for them.
                let tweetTypeFilter = _.map((me.getProp('tweetTypeFilter') || '').toString().split(','), Number);
                if (_.includes(tweetTypeFilter, RivalIQ.enums.tweetTypes.retweet.id)) {
                    tweetTypeFilter = _.without(tweetTypeFilter, RivalIQ.enums.tweetTypes.retweet.id);

                    if (tweetTypeFilter.length) {
                        observationMetricBase = RivalIQ.metric.build(me.dashboardModel.baseMetric, {
                            tweetTypeFilter: tweetTypeFilter.join(',')
                        });
                    } else {
                        observationMetrics = [];
                    }
                }
            }
            const metrics = [pieChartMetric].concat(RivalIQ.metric.build(observationMetricBase, observationMetrics));

            return $.when.apply(undefined, _.map(metrics, function (m) { return new RivalIQ.models.MetricDefinition(m).fetch(); }))
                .then(function () {
                    me.pieChartMetric = _.head(arguments);
                    me.observationMetrics = _.reduce(_.tail(arguments), function (memo, metric, idx) {
                        memo[observationMetrics[idx].property] = metric;
                        return memo;
                    }, {});
                });
        },

        onBeforeRenderLoaded: function () {
            const me = this;

            me.showTable();
        },

        onAttachLoaded: function () {
            const me = this;

            if (me.getProp('onlyShowTable')) {
                me.ui.analysisSection.remove();
                return;
            }

            me.showSummaries();
            me.showObservations();
        },

        showTable: function () {
            const me = this;

            // need to be strictly equal here, undefined is the default case. This is used for PPT export.
            if (me.getProp('onlyShowTable') === false) {
                return;
            }

            const widget = me.dashboardModel.getWidget('table');
            me.table.showWidget(widget, {
                className: `table-double-header ${me.dashboardModel.isExporting() ? 'x-table-container' : 'table-responsive'}`
            });

            me.listenTo(widget, 'change:sortOrder', function (model, sortOrder) {
                me.setProps('sortOrder', sortOrder, {
                    silent: false
                });
            });

            me.listenTo(widget, 'change:orderBy', function (model, orderBy) {
                me.setProps('orderBy', orderBy, {
                    silent: false
                });
            });
        },

        _generatePostObservations: function () {
            const me = this;

            const metricsToCheck = _.values(getPostObservationMetrics(me.getProp('source')));
            const observations = _.reduce(metricsToCheck, function (memo, metricName) {
                let metricResults;

                // We might not have had a relevant observation metric to pull, so observationMetrics[metricName] might be undefined.
                if (me.observationMetrics[metricName]) {
                    metricResults = _.orderBy(me.observationMetrics[metricName].getValue({
                        format: false
                    }), [_.last], ['desc']);
                }

                if (_.size(metricResults) > 1) {
                    const highestMetricType = metricResults[0][0];

                    const metricsByType = _.reduce(metricResults, function (byTypeMemo, metricResult) {
                        byTypeMemo[metricResult[0]] = {};
                        byTypeMemo[metricResult[0]][metricName] = _.last(metricResult);

                        return byTypeMemo;
                    }, {});

                    _.each(me.observationMetrics.count.getValue({
                        format: false
                    }), function (metricResult) {
                        metricsByType[metricResult[0]].numPosts = _.last(metricResult);
                    });

                    if (metricsByType[highestMetricType].numPosts < MIN_NUM_POSTS_PER_BUCKET_FOR_OBSERVATION) {
                        return memo;
                    }

                    // add in an other bucket too.
                    metricsByType.other = calculateOtherBucket(me.getProp('source'), me.observationMetrics, metricsByType, highestMetricType);

                    const metric = RivalIQ.apps.Insights.utils.createInsightsMetric('post', _.defaults({
                        property: metricName,
                        dimensions: ['timeAnalysisMode', 'channel', 'companyId']
                    }, me.getProps()));
                    const name = RivalIQ.metric.names.metricName(metric);

                    let observation = null;

                    // first, let's try to make an observation with the highest bucket vs the other.
                    let deltaPct = (metricsByType[highestMetricType][metricName] - metricsByType.other[metricName]) / metricsByType.other[metricName];
                    if (metricResults.length > 2 && metricsByType.other.numPosts >= MIN_NUM_POSTS_PER_BUCKET_FOR_OBSERVATION && deltaPct >= MIN_POST_OBSERVATION_DELTA_PCT) {
                        observation = {
                            observationClass: 'observation-positive',
                            name: name
                        };

                        observation.text = `The <strong>${name.toLowerCase()} for ${pluralize(highestMetricType.toLowerCase())} is ${
                            RivalIQ.formatter.absolutePercent(deltaPct)} higher</strong> than that on other post types.`;
                        observation.description = `We analyzed ${name.toLowerCase()} on your content and found an outlier in your data.`;
                    } else {
                        // nothing exciting about other, let's see if the highest is bigger than the second highest.
                        const secondHighestMetricType = metricResults[1][0];
                        deltaPct = (metricsByType[highestMetricType][metricName] - metricsByType[secondHighestMetricType][metricName]) / metricsByType[secondHighestMetricType][metricName];

                        if (metricsByType[secondHighestMetricType].numPosts >= MIN_NUM_POSTS_PER_BUCKET_FOR_OBSERVATION && deltaPct >= MIN_POST_OBSERVATION_DELTA_PCT) {

                            observation = {
                                observationClass: 'observation-positive',
                                name: name
                            };

                            observation.text = `The <strong>${name.toLowerCase()} for ${pluralize(highestMetricType.toLowerCase())} is ${
                                RivalIQ.formatter.absolutePercent(deltaPct)} higher</strong> than that on ${pluralize(secondHighestMetricType.toLowerCase())}.`;
                            observation.description = `We analyzed ${name.toLowerCase()} on your content and found an outlier in your data.`;
                        }
                    }

                    if (observation) {
                        memo.push(observation);
                    }
                }

                return memo;
            }, []);

            return _.take(observations, MAX_POST_OBSERVATIONS);
        },

        _generateStoryObservations: function () {
            const me = this;

            const observations = [];

            // Unless this is a storyDay detail page, there's a minimum number of days with stories that you need for meaningful insights.
            if (!me.getProp('storyDay') && me.observationMetrics.days_with_story.getValue({
                format: false
            }) < MIN_NUM_STORY_DAYS_FOR_OBSERVATION) {
                return observations;
            }

            const numStoriesByType = _(me.observationMetrics.count.getValue({
                format: false
            }))
                .keyBy(_.head)
                .mapValues(_.last)
                .value();

            if (numStoriesByType.Video >= MIN_NUM_STORIES_FOR_OBSERVATION && numStoriesByType.Photo >= MIN_NUM_STORIES_FOR_OBSERVATION) {

                const tapsForwardRateByType = _.keyBy(me.observationMetrics.average_taps_forward_rate.getValue({
                    format: false
                }), _.head);
                if (_.size(tapsForwardRateByType) > 1) {
                    const videoRate = tapsForwardRateByType.Video[1];
                    const photoRate = tapsForwardRateByType.Photo[1];

                    if (photoRate) {
                        const deltaVideoPct = (videoRate - photoRate) / photoRate;
                        if (videoRate > photoRate && deltaVideoPct > MIN_TAPS_FORWARD_PER_STORY_DELTA_PCT) {
                            observations.push({
                                observationClass: 'observation-negative',
                                name: 'Video Engagement',
                                description: RivalIQ.strings.insights_by_type_video_engagement_insight_description,
                                text: `<strong>Viewers tap forward through your videos at a ${RivalIQ.formatter.absolutePercent(deltaVideoPct)} higher rate</strong> than they do on photos.`
                            });
                        }
                    }

                    if (videoRate) {
                        const deltaPhotoPct = (photoRate - videoRate) / videoRate;
                        if (photoRate > videoRate && deltaPhotoPct > MIN_TAPS_FORWARD_PER_STORY_DELTA_PCT) {
                            observations.push({
                                observationClass: 'observation-positive',
                                name: 'Video Engagement',
                                description: RivalIQ.strings.insights_by_type_video_engagement_insight_description,
                                text: `<strong>Viewers tap forward through your videos at a ${RivalIQ.formatter.absolutePercent(deltaPhotoPct)} lower rate</strong> than they do on photos.`
                            });
                        }
                    }
                }

                const exitRateByType = _.orderBy(me.observationMetrics.exits_per_story.getValue({
                    format: false
                }), [_.last], ['desc']);
                if (_.size(exitRateByType) > 1) {
                    const exitRatePct = Math.abs(exitRateByType[1][1] - exitRateByType[0][1]) / Math.min(exitRateByType[1][1], exitRateByType[0][1]);
                    if (_.isFinite(exitRatePct) && exitRatePct > MIN_STORY_EXIT_RATE_DELTA_PCT) {
                        const highExitType = exitRateByType[0][0];
                        const lowExitType = exitRateByType[1][0];

                        observations.push({
                            observationClass: 'observation-negative',
                            name: 'Exit rate / frame',
                            description: RivalIQ.strings.insights_by_type_exit_rate_insight_description,
                            text: `<strong>Viewers are exiting your ${pluralize(highExitType.toLowerCase())} at a ${
                                RivalIQ.formatter.absolutePercent(exitRatePct)} higher rate </strong>` +
                              ` than the exit rate for ${pluralize(lowExitType.toLowerCase())}.`
                        });
                    }
                }
            }

            if (numStoriesByType.Video >= MIN_NUM_VIDEO_STORIES_FOR_OBSERVATION) {
                const totalImpressions = me.observationMetrics.total_impressions.getValue({
                    format: false
                });
                const totalExits = me.observationMetrics.total_exits.getValue({
                    format: false
                });
                const totalTapsForward = me.observationMetrics.total_taps_forward.getValue({
                    format: false
                });

                const totalViewRate = (totalImpressions - totalExits - totalTapsForward) / totalImpressions;

                if (totalViewRate > 1 - VIDEO_TOTAL_COMPLETION_RATE_THRESHOLD) {
                    observations.push({
                        observationClass: 'observation-positive',
                        name: 'Full View Rate by Impression',
                        description: RivalIQ.strings.insights_by_type_video_view_rate_insight_description,
                        text: `<strong>${RivalIQ.formatter.absolutePercent(totalViewRate)} of video views stayed through the entire video</strong> (no exits and taps forward). Keep it up!`
                    });
                } else if (totalViewRate < VIDEO_TOTAL_COMPLETION_RATE_THRESHOLD) {
                    observations.push({
                        observationClass: 'observation-negative',
                        name: 'Full View Rate by Impression',
                        description: RivalIQ.strings.insights_by_type_video_view_rate_insight_description,
                        text: `Only <strong>${RivalIQ.formatter.absolutePercent(totalViewRate)} of views on your videos were engaged through the entire video</strong> (no exits and taps forward).`
                    });
                }
            }

            return observations;
        },

        showObservations: function () {
            const me = this;

            let observations;

            if (me.getProp('objectType') === RivalIQ.enums.insightsObjectType.post) {
                observations = me._generatePostObservations();
            } else {
                observations = me._generateStoryObservations();
            }

            const observationsView = new ObservationsView({
                observations: observations
            });

            me.observationsRegion.show(observationsView);
        },

        getChart: function (metrics, data) {
            const view = new RivalIQ.graph.PieChart({
                height: PIE_CHART_SIZE,
                width: PIE_CHART_SIZE,
                dataProperty: 'data',
                data: data,
                concept: {
                    color: function (datum) {
                        return getColor(datum[0]);
                    },
                    getClassName: _.constant(null)
                }
            });

            return view;
        },

        getLegend: function (metrics) {
            const me = this;

            const legend = new RivalIQ.apps.Dashboard.views.SummaryItems({
                childView: PieChartSummaryItem,
                collection: metrics,
                tagName: 'div',
                childViewOptions: function () {
                    const summaryItemsMe = this;

                    const options = RivalIQ.apps.Dashboard.views.SummaryItems.prototype.childViewOptions.apply(summaryItemsMe, arguments);

                    return _.extend(options, {
                        dashboardModel: me.dashboardModel
                    });
                }
            });

            return legend;
        },

        hasLegend: _.constant(true),

        showSummaries: function () {
            const me = this;

            const data = me.pieChartMetric.getValue({
                format: false
            });

            const DataCollection = Backbone.Collection.extend({
                model: function (datum) {
                    return new PieLegendModel({
                        name: datum[0],
                        value: datum[1]
                    });
                }
            });

            const metrics = new DataCollection(data);

            if (me.hasLegend()) {
                const legend = me.getLegend(metrics);
                if (legend) {
                    me.analysisChartLegend.show(legend);
                }
            }

            const chart = me.getChart(metrics, data);
            me.analysisChart.show(chart);
        },

        serializeData: function () {
            const me = this;

            return {
                objectName: getPluralObjectName(me.getProp('objectType'), me.getProp('source'))
            };
        }
    });


    const ByTypeWidget = widgets._ByTypeWidget = RivalIQ.apps.Dashboard.models.Widget.mixin({

        mixins: {
            exportHD: RivalIQ.apps.Dashboard.mixins.ExportHD
        },

        getExportTitle: App.utils.insightsExportTitle,
        getExportSubtitle: App.utils.insightsExportSubtitle,
        getPptSubtitle: App.utils.insightsPptSubtitle,

        panelView: App.views.ManageColumnsPanel,
        detailPanelView: App.views.ManageColumnsPanel,

        exportFormats: [
            RivalIQ.enums.exportFormat.PPT,
            RivalIQ.enums.exportFormat.PDF,
            RivalIQ.enums.exportFormat.PNG
            // RivalIQ.enums.exportFormat.CSV
        ],

        scheduledExportAdvancedSettingsEnabled: false,

        title: function () {
            const me = this;

            if (me.getProp('objectType') === RivalIQ.enums.insightsObjectType.post) {
                return 'Post Type';
            } else {
                return 'Frame Type';
            }
        },

        description: function () {
            const me = this;

            if (me.getProp('objectType') === RivalIQ.enums.insightsObjectType.post) {
                const postWord = RivalIQ.data.channels.get(me.getProp('channel')).get('postName');
                return RivalIQ.str('insights_posts_by_type_description', postWord);
            } else {
                return RivalIQ.strings.insights_by_type_description;
            }
        },

        getView: function () {
            return ByTypeAnalysisView;
        },

        processProps: function (props) {
            const me = this;

            _.defaults(props, {
                analysisProperty: 'mediaType',
                sortOrder: 1,
                orderBy: 1,
                limit: 4,
                objectType: RivalIQ.enums.insightsObjectType.story
            });

            // if we're in details view show all the rows.
            if (props.instanceId || props.widgetId) {
                props.limit = -1;
            }

            if (props.source === RivalIQ.enums.sources.INSTAGRAM_INSIGHTS && _.isEmpty(props.post_metrics)) {
                const hasPaidEngagement = App.utils.hasInstagramPaidEngagement(me);
                if (hasPaidEngagement) {
                    props.post_metrics = [
                        'count',
                        'organic_reach_per_post',
                        'engagement_rate_by_impression'
                    ];
                } else {
                    props.post_metrics = [
                        'count',
                        'organic_reach_per_post',
                        'organic_engagement_rate_by_impression'
                    ];
                }

                if (_.isEmpty(me.configurationProps.post_metrics)) {
                    me.configurationProps.post_metrics = props.post_metrics;
                }
            }

            me.baseMetric = App.utils.createInsightsMetric(props.objectType, _.defaults({
                dimensions: ['timeAnalysisMode', 'channel', 'companyId']
            }, props));

            return props;
        },

        widgetDefinitions: function () {
            const me = this;

            return [{
                instanceId: 'table',
                widgetId: RivalIQ.enums.widgetTypes.INSIGHTS_BY_TYPE_TABLE,
                neverFilter: true,
                baseMetric: me.baseMetric,
                props: {
                    showDescriptions: true,
                    omitTimeAnalysisModeDescription: true
                }
            }];
        },

        allowPageBreaks: function () {
            const me = this;

            const limit = me.getProp('limit');
            return limit === -1;
        },

        getSlides: function () {
            const me = this;

            return [
                me.buildSlide({
                    onlyShowTable: false
                }),
                me.buildSlide({
                    onlyShowTable: true
                })
            ];
        },

        filter: function () {
            const me = this;

            const objectType = me.getProp('objectType');
            const isPostObjectType = (objectType === RivalIQ.enums.insightsObjectType.post);

            return (isPostObjectType && RivalIQ.apps.Insights.utils.isLoadingInstagramPromotedPosts(me));
        }

        // csvExportProps: function (props) {
        //     let me = this;
        //
        //     return me.getWidget('table').csvExportProps(props);
        // },
    });


    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.INSIGHTS_BY_TYPE, ByTypeWidget);

});
